import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { Product } from '../../../../../shared/models/product.model';
import { isSingleProduct } from '../../../../../shared/utils/product.utils';
import { BlogArticle, ProductTab } from '../../models/product-detail.model';
import { IconComponent } from '../../../../../shared/ui/icon/icon.component';
import { ChevronDown, LucideIconData } from 'lucide-angular';

interface TabDef {
  id: ProductTab;
  label: string;
}

@Component({
  selector: 'app-product-tabs',
  imports: [IconComponent, NgTemplateOutlet],
  templateUrl: './product-tabs.component.html',
  styleUrl: './product-tabs.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductTabsComponent {
  product = input.required<Product>();
  articles = input<BlogArticle[]>([]);

  protected readonly chevronDown: LucideIconData = ChevronDown;

  protected readonly activeTab = signal<ProductTab>('description');
  protected readonly isMobile = signal(false);
  protected readonly openAccordion = signal<ProductTab | null>('description');

  private readonly destroyRef = inject(DestroyRef);

  protected readonly availableTabs = computed<TabDef[]>(() => {
    const p = this.product();
    const arts = this.articles();
    const tabs: TabDef[] = [{ id: 'description', label: 'Descripción' }];

    // Fase 3C: el tab se muestra para cualquier producto con specs asignadas.
    // Antes estaba limitado a product_type === 'accessory'; ahora que las specs
    // vienen de atributos reutilizables por cualquier tipo, se relaja la condición.
    if (p.specs && Object.keys(p.specs).length > 0) {
      tabs.push({ id: 'specs', label: 'Características' });
    }

    if (arts.length > 0) {
      tabs.push({ id: 'articles', label: 'Artículos relacionados' });
    }

    return tabs;
  });

  protected readonly specEntries = computed(() =>
    Object.entries(this.product().specs ?? {}),
  );

  protected readonly isSingle = computed(() => isSingleProduct(this.product()));

  protected readonly isSealed = computed(() =>
    this.product().productType === 'sealed',
  );

  protected readonly isAccessory = computed(() =>
    this.product().productType === 'accessory',
  );

  constructor() {
    afterNextRender(() => {
      const query = window.matchMedia('(max-width: 47.9375rem)');
      const sync = (matches: boolean): void => this.isMobile.set(matches);
      sync(query.matches);

      const onChange = (e: MediaQueryListEvent): void => sync(e.matches);
      query.addEventListener('change', onChange);
      this.destroyRef.onDestroy(() => query.removeEventListener('change', onChange));
    });
  }

  protected setTab(tab: ProductTab): void {
    this.activeTab.set(tab);
  }

  protected toggleAccordion(tab: ProductTab): void {
    this.openAccordion.update((current) => (current === tab ? null : tab));
  }

  protected isAccordionOpen(tab: ProductTab): boolean {
    return this.openAccordion() === tab;
  }
}
